/**
 * The console shell: session, tenant, theme, navigation.
 *
 * The session lives here and nowhere else. Every page reads it through `useSession`, so there is
 * one answer to "who am I and what may I do" and no screen can drift from it — which matters
 * because the capability list is what decides whether a destructive button is rendered at all.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { api, onUnauthorized, type Me, type Overview } from './api'
import { Note, ToastProvider, useAsync, usePoll, useToast } from './components/ui'

import Dashboard from './pages/Dashboard'
import Incidents from './pages/Incidents'
import IncidentDetail from './pages/IncidentDetail'
import Explore from './pages/Explore'
import Sources from './pages/Sources'
import Onboarding from './pages/Onboarding'
import Rules from './pages/Rules'
import Playbooks from './pages/Playbooks'
import PlaybookEditor from './pages/PlaybookEditor'
import Intel from './pages/Intel'
import Requirements from './pages/Requirements'
import Coverage from './pages/Coverage'
import Channels from './pages/Channels'
import MalwareAnalysis from './pages/MalwareAnalysis'
import Settings from './pages/Settings'
import Login from './pages/Login'

/* ------------------------------------------------------------------ session */

interface SessionValue {
  me: Me
  refresh: () => void
  can: (capability: string) => boolean
  signOut: () => void
}

const SessionCtx = createContext<SessionValue | null>(null)

export function useSession(): SessionValue {
  const v = useContext(SessionCtx)
  if (!v) throw new Error('useSession outside a session')
  return v
}

/* ------------------------------------------------------------------ theme */

type Theme = 'system' | 'light' | 'dark'

function readTheme(): Theme {
  try {
    const v = localStorage.getItem('bastion.theme')
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    /* private mode */
  }
  return 'system'
}

function applyTheme(theme: Theme) {
  const dark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
  try {
    localStorage.setItem('bastion.theme', theme)
  } catch {
    /* private mode: the choice simply does not persist */
  }
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(readTheme)
  useEffect(() => {
    applyTheme(theme)
    if (theme !== 'system') return
    // Follow the system while set to system, so a machine that switches at dusk switches with it.
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])
  return [theme, setTheme]
}

/* ------------------------------------------------------------------ shell */

function BastionMark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
      <path d="M16 2 4 7v9c0 7.5 5.1 13.4 12 14.9 6.9-1.5 12-7.4 12-14.9V7L16 2Z" fill="var(--accent)" />
      <path d="M16 8v14M10 12v8M22 12v8" stroke="var(--surface)" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  )
}

function Nav({ overview }: { overview: Overview | null }) {
  const { me, can } = useSession()

  const link = (to: string, label: string, count?: number) => (
    <NavLink to={to} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} end={to === '/'}>
      <span>{label}</span>
      {count !== undefined && count > 0 && <span className="count tnum">{count}</span>}
    </NavLink>
  )

  return (
    <nav className="nav">
      <div className="nav-brand">
        <BastionMark size={20} />
        Bastion
      </div>

      <div className="nav-section">Operate</div>
      {link('/', 'Dashboard')}
      {link('/incidents', 'Incidents', overview?.open_incidents)}
      {link('/approvals', 'Approvals', overview?.runs_awaiting_approval)}
      {link('/channels', 'Channels')}
      {link('/explore', 'Explore')}

      <div className="nav-section">Intelligence</div>
      {link('/requirements', 'Requirements')}
      {link('/intel', 'Feeds & indicators')}
      {link('/coverage', 'ATT&CK coverage')}

      <div className="nav-section">Engineer</div>
      {link('/sources', 'Log sources', overview?.sources_configured)}
      {link('/rules', 'Detection rules')}
      {link('/playbooks', 'Playbooks')}
      {link('/sandbox', 'Malware analysis')}

      <div className="nav-foot">
        <NavLink to="/settings" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
          Settings
        </NavLink>
        <div className="small faint" style={{ padding: '6px 9px' }}>
          {me.display_name}
          <br />
          <span className="faint">{me.roles.join(', ')}</span>
          {!can('write_incidents') && (
            <>
              <br />
              <span className="faint">read-only</span>
            </>
          )}
        </div>
      </div>
    </nav>
  )
}

function TenantSwitcher() {
  const { me, refresh } = useSession()
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  if (me.available_tenants.length <= 1) {
    return (
      <span className="chip outline" title="Your workspace">
        {me.tenant_display_name || me.tenant}
      </span>
    )
  }
  return (
    <label style={{ margin: 0 }}>
      <span className="visually-hidden" />
      <select
        aria-label="Workspace"
        value={me.tenant}
        disabled={busy}
        style={{ width: 'auto', minWidth: 190 }}
        onChange={(e) => {
          const tenant = e.target.value
          setBusy(true)
          api
            .post('/auth/switch-tenant', { tenant })
            .then(() => {
              toast('ok', `Switched to ${tenant}`)
              refresh()
            })
            .catch((err: Error) => toast('error', err.message))
            .finally(() => setBusy(false))
        }}
      >
        {me.available_tenants.map((t) => (
          <option key={t.slug} value={t.slug}>
            {t.display_name}
          </option>
        ))}
      </select>
    </label>
  )
}

function ThemeToggle() {
  const [theme, setTheme] = useTheme()
  const next: Theme = theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark'
  const label = theme === 'dark' ? 'Dark' : theme === 'light' ? 'Light' : 'System'
  const glyph = theme === 'dark' ? '◑' : theme === 'light' ? '○' : '◐'
  return (
    <button className="ghost sm" onClick={() => setTheme(next)} title={`Theme: ${label}. Click for ${next}.`}>
      <span aria-hidden>{glyph}</span> {label}
    </button>
  )
}

function Shell() {
  const { me, signOut } = useSession()
  const location = useLocation()
  const overview = useAsync<Overview>(() => api.get<Overview>('/overview'), [me.tenant])

  // A live console: the nav counts and the dashboard should move without the analyst reloading.
  usePoll(overview.reload, 15000)

  return (
    <div className="app">
      <Nav overview={overview.data} />
      <div className="main">
        <header className="header">
          <TenantSwitcher />
          {me.env !== 'prod' && (
            <span className="chip warn" title="This deployment is not in production mode">
              {me.env}
            </span>
          )}
          {overview.data && overview.data.detection_degradations > 0 && (
            <NavLink to="/settings" className="chip danger" title="Detection is currently degraded">
              detection degraded
            </NavLink>
          )}
          <div className="spacer" />
          <ThemeToggle />
          <button className="ghost sm" onClick={signOut}>
            Sign out
          </button>
        </header>
        <main className="content" key={location.pathname}>
          <Routes>
            <Route path="/" element={<Dashboard overview={overview.data} reload={overview.reload} />} />
            <Route path="/incidents" element={<Incidents />} />
            <Route path="/incidents/:id" element={<IncidentDetail />} />
            <Route path="/approvals" element={<Incidents approvalsOnly />} />
            <Route path="/channels" element={<Channels />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/requirements" element={<Requirements />} />
            <Route path="/intel" element={<Intel />} />
            <Route path="/coverage" element={<Coverage />} />
            <Route path="/sources" element={<Sources />} />
            <Route path="/sources/new" element={<Onboarding />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/playbooks" element={<Playbooks />} />
            <Route path="/playbooks/:id" element={<PlaybookEditor />} />
            <Route path="/sandbox" element={<MalwareAnalysis />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ root */

export default function App() {
  const [me, setMe] = useState<Me | null>(null)
  const [checked, setChecked] = useState(false)
  const [offline, setOffline] = useState<string | null>(null)

  const load = useCallback(() => {
    api
      .get<Me>('/auth/me')
      .then((m) => {
        setMe(m)
        setOffline(null)
      })
      .catch((e: Error & { status?: number }) => {
        setMe(null)
        // Distinguish "not signed in" from "the control plane is not answering". Presenting a
        // login form when the server is down sends the reader looking for a password problem
        // they do not have.
        if (e.status === 0) setOffline(e.message)
      })
      .finally(() => setChecked(true))
  }, [])

  useEffect(load, [load])

  useEffect(() => onUnauthorized(() => setMe(null)), [])

  const signOut = useCallback(() => {
    api
      .post('/auth/logout')
      .catch(() => {})
      .finally(() => setMe(null))
  }, [])

  const value = useMemo<SessionValue | null>(
    () =>
      me
        ? {
            me,
            refresh: load,
            can: (c: string) => me.capabilities.includes(c),
            signOut,
          }
        : null,
    [me, load, signOut],
  )

  if (!checked) return <div className="empty muted">Loading…</div>

  return (
    <ToastProvider>
      {offline && !me && (
        <div style={{ maxWidth: 520, margin: '18vh auto', padding: '0 20px' }}>
          <Note tone="danger">
            {offline}
            <div style={{ marginTop: 8 }}>
              <button className="sm" onClick={load}>
                Try again
              </button>
            </div>
          </Note>
        </div>
      )}
      {!offline && !value && <Login onSignedIn={load} />}
      {value && (
        <SessionCtx.Provider value={value}>
          <Shell />
        </SessionCtx.Provider>
      )}
    </ToastProvider>
  )
}
